import {
  datadogAllowProps,
  generateDatadogTrackingProps,
} from 'toolkit/environment/environment/userMonitoring.util';
import { SyntheticEvent } from 'react';
import { ActionMenu } from '../../../../utilities/action-menu/ActionMenu';
import { CollapsibleCardProps } from '../types';
import { Box, CardHeader, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

/** Props for the header and toggle control of a collapsible card. */
export type CollapsibleCardHeaderProps = Pick<
  CollapsibleCardProps,
  | 'title'
  | 'actions'
  | 'secondaryActions'
  | 'avatar'
  | 'badge'
  | 'chips'
  | 'collapsible'
  | 'endContent'
  | 'onToggle'
  | 'trackingName'
  | 'resolveTrackingName'
  | 'allowReplayOnTitle'
  | 'onToggleOpen'
  | 'titleTypographyVariant'
  | 'isCompact'
  | 'hideExpander'
  | 'shouldShowPointer'
> & {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  rightComponent?: JSX.Element;
};

/**
 * Header row for CollapsibleCard: title, optional avatar and actions,
 * and an expand/collapse control when `collapsible` is set. When collapsible
 * with a visible chevron, the title row toggles on pointer click; keyboard
 * users expand/collapse via the chevron `IconButton`. When `hideExpander`,
 * the whole header acts as the toggle and carries `aria-expanded`.
 * @param props - Header configuration and optional toggle callbacks
 * @returns MUI `CardHeader` configured for a collapsible card
 */
export const CollapsibleCardHeader = ({
  title,
  actions,
  secondaryActions,
  avatar,
  badge,
  chips,
  titleTypographyVariant,
  isCompact = false,
  hideExpander = false,
  shouldShowPointer = false,
  collapsible,
  endContent = null,
  open,
  rightComponent,
  setOpen,
  onToggle,
  trackingName,
  resolveTrackingName,
  allowReplayOnTitle = false,
  onToggleOpen,
}: CollapsibleCardHeaderProps) => {
  const resolvedActionName = resolveTrackingName
    ? resolveTrackingName(Boolean(open))
    : undefined;
  const sectionTrackingProps = resolvedActionName
    ? generateDatadogTrackingProps(resolvedActionName)
    : undefined;
  /**
   * Expands or collapses the card and runs optional parent callbacks.
   */
  const handleToggle = (): void => {
    if (!setOpen) {
      return;
    }
    setOpen(!open);
    if (onToggle) {
      onToggle();
    }
    if (onToggleOpen && !open) {
      onToggleOpen();
    }
  };

  /**
   * Prevents header-level toggle when interacting with the action column
   * (kebab menu, expand control, or custom end content).
   * @param event - DOM event from the action region
   */
  const handleActionRegionClick = (event: SyntheticEvent): void => {
    event.stopPropagation();
  };

  return (
    <CardHeader
      {...(sectionTrackingProps ?? {})}
      title={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '24px',
            rowGap: '18px',
            flexWrap: 'wrap',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: isCompact ? '12px' : '24px',
              flexGrow: 1,
            }}
          >
            {avatar}
            <Typography
              variant={titleTypographyVariant}
              {...(allowReplayOnTitle ? datadogAllowProps : {})}
            >
              {title}
            </Typography>
            {badge && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'center',
                }}
              >
                {badge}
              </Box>
            )}
            {rightComponent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'end',
                  flexGrow: 1,
                  flexDirection: 'column',
                }}
              >
                {rightComponent}
              </Box>
            )}
          </Box>
          {chips && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                columnGap: '8px',
                rowGap: '0px',
                flexWrap: 'wrap',
              }}
            >
              {chips}
            </Box>
          )}
        </Box>
      }
      action={
        <Box
          onClick={handleActionRegionClick}
          sx={{ display: 'flex', height: '100%', alignItems: 'center' }}
        >
          {actions ? (
            <ActionMenu
              actions={actions}
              secondaryActions={secondaryActions}
              horizontalAlign="right"
            />
          ) : null}
          {endContent && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                height: '100%',
                ...(hideExpander && { paddingTop: '12px' }),
              }}
            >
              {endContent}
            </Box>
          )}
          {collapsible && !hideExpander && (
            <IconButton
              {...generateDatadogTrackingProps(
                resolvedActionName ??
                  trackingName ??
                  'collapsible-card-toggle',
              )}
              onClick={handleToggle}
              aria-expanded={Boolean(open)}
              aria-label={title}
            >
              {open ? (
                <KeyboardArrowUpIcon aria-hidden />
              ) : (
                <KeyboardArrowDownIcon aria-hidden />
              )}
            </IconButton>
          )}
        </Box>
      }
      onClick={collapsible ? handleToggle : undefined}
      aria-expanded={collapsible && hideExpander ? Boolean(open) : undefined}
      sx={{
        ...((shouldShowPointer || (collapsible && !hideExpander)) && {
          '&:hover': { cursor: 'pointer' },
        }),
      }}
    />
  );
};
